import { useCallback, useEffect } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { useReloadToast } from 'components/ReloadToast';
import { Hub } from 'data/auth/amplify';
import { idTokenAtom } from 'data/auth/atoms/idTokenAtom';
import {
  removeInitialRoute,
  useStoreInitialRoute,
} from 'data/auth/hooks/initialRoute';
import { useNextPathRedirect } from 'data/auth/hooks/useNextPathRedirect';
import { useNavigate } from 'utils/navigation';
import { EntryPoint } from 'utils/nextTypes';
import paths from 'utils/paths';
import { isError, isSuccess } from 'utils/remote';

let customOAuthState: string | undefined;

Hub.listen('auth', async ({ payload }) => {
  const { event, data } = payload;

  switch (event) {
    case 'customOAuthState':
      customOAuthState = data;
      break;
  }
});

const refreshSession = async () => {
  try {
    const session = await Auth.currentSession();
    return session;
  } catch (error) {
    return null;
  }
};

function useProtectedPagesGuard() {
  const navigate = useNavigate();
  const [idTokenRemote, setIdTokenRemote] = useAtom(idTokenAtom);
  const storeInitialRoute = useStoreInitialRoute();
  const redirectToNextPath = useNextPathRedirect();

  const { t } = useTranslation('auth');
  const reloadToast = useReloadToast(
    t('authentication_error_toast_title'),
    t('authentication_error_toast_description'),
  );

  const restoreSession = useCallback(
    async (session: CognitoUserSession | null) => {
      try {
        if (!session) {
          throw new Error('Unauthorised');
        }
        const token = session.getIdToken();
        const idToken = token.getJwtToken();
        const expiry = token.getExpiration();

        setIdTokenRemote({
          data: { idToken, expiry },
          fetchStatus: 'SUCCESS',
        });
      } catch (error) {
        storeInitialRoute();
        navigate(paths.account.login);
      }
    },
    [setIdTokenRemote, storeInitialRoute, navigate],
  );

  return useCallback(async () => {
    const session = await refreshSession();
    if (isError(idTokenRemote)) {
      if (
        idTokenRemote.errorMessage ===
        'Something went wrong: "The user is not authenticated"'
      ) {
        restoreSession(session);
        return;
      }
      reloadToast();
      return;
    }

    if (isSuccess(idTokenRemote)) {
      if (!idTokenRemote.data.idToken) {
        restoreSession(session);
      }

      if (customOAuthState) {
        const data = customOAuthState;
        customOAuthState = undefined;
        removeInitialRoute();
        redirectToNextPath(data);
        return;
      }
    }
  }, [idTokenRemote, reloadToast, redirectToNextPath, restoreSession]);
}

function useAuthPagesGuard(initialAccessPath?: string) {
  const navigate = useNavigate();
  const idTokenRemote = useAtomValue(idTokenAtom);

  return useCallback(() => {
    if (isSuccess(idTokenRemote) && !!idTokenRemote.data.idToken) {
      navigate(initialAccessPath ?? paths.account.index);
    }
  }, [idTokenRemote, initialAccessPath, navigate]);
}

export function useAccessGuard(
  entryPointType: EntryPoint,
  initialAccessPath?: string,
): void {
  const protectedPagesGuard = useProtectedPagesGuard();
  const authPagesGuard = useAuthPagesGuard(initialAccessPath);

  useEffect(() => {
    switch (entryPointType) {
      case EntryPoint.PROTECTED:
        protectedPagesGuard();
        return;
      case EntryPoint.AUTH:
        authPagesGuard();
        return;
    }
  }, [entryPointType, authPagesGuard, protectedPagesGuard]);
}
